// TradeCalendar.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Box, Typography, Modal, useTheme } from '@mui/material';
import AddTrade from './AddTrade';
import TradeForm from '../components/TradeForm';

const TradeCalendar = ({ trades = [] }) => {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [addTradeOpen, setAddTradeOpen] = useState(false);

  useEffect(() => {
    const tradeEvents = transformTradesToEvents(trades);
    setEvents(tradeEvents);
  }, [trades]);

  const fetchTrades = async () => {
    // This function is kept for modal refresh functionality
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Don't set events here as they come from props
      return response.data;
    } catch (error) {
      console.error('Error fetching trades:', error);
      return [];
    }
  };


const transformTradesToEvents = (trades = []) => {
  const dailySummary = trades.reduce((acc, trade) => {
    const date = trade.tradeDate;
    if (!acc[date]) {
      acc[date] = { wins: 0, losses: 0, breakeven: 0, open: 0, analysis: 0, totalPnL: 0, trades: [], strategies: new Set() };
    }
    
    acc[date].trades.push(trade);
    acc[date].totalPnL += parseFloat(trade.profitLoss || 0);
    
    // Add strategy names
    if (trade.strategyNames) {
      if (Array.isArray(trade.strategyNames)) {
        trade.strategyNames.forEach(name => acc[date].strategies.add(name));
      } else {
        acc[date].strategies.add(trade.strategyNames);
      }
    }
    
    switch(trade.status) {
      case 'WIN': acc[date].wins++; break;
      case 'LOSS': acc[date].losses++; break;
      case 'BREAKEVEN': acc[date].breakeven++; break;
      case 'OPEN': acc[date].open++; break;
      case 'ANALYSIS': acc[date].analysis++; break;
    }
    
    return acc;
  }, {});

  return Object.entries(dailySummary).map(([date, summary]) => {
    const totalTrades = summary.wins + summary.losses + summary.breakeven + summary.open + summary.analysis;
    const winRate = summary.wins + summary.losses > 0 ? ((summary.wins / (summary.wins + summary.losses)) * 100).toFixed(0) : 0;
    const strategyList = Array.from(summary.strategies).join(', ') || 'No Strategy';
    
    let color = '#2196f3';
    if (summary.totalPnL > 0) color = '#4caf50';
    else if (summary.totalPnL < 0) color = '#f44336';
    else if (summary.breakeven > 0) color = '#ff9800';

    return {
      id: date,
      title: `${strategyList}\n${totalTrades} trades W:${summary.wins} L:${summary.losses}\nWR:${winRate}% P&L:$${summary.totalPnL.toFixed(0)}`,
      date,
      color,
      extendedProps: { summary, trades: summary.trades },
    };
  });
};



  const renderEventContent = (eventInfo) => (
    <Box sx={{ 
      fontSize: '0.7rem', 
      whiteSpace: 'pre-line', 
      color: '#fff',
      fontWeight: 600,
      p: 0.8,
      borderRadius: 2,
      backgroundColor: eventInfo.event.backgroundColor,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      border: '1px solid rgba(255,255,255,0.1)',
      backdropFilter: 'blur(10px)'
    }}>
      {eventInfo.event.title}
    </Box>
  );

  // Event clicks disabled for dashboard view

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          📅 Calendar View
        </Typography>
      </Box>

      <Box sx={{ 
        '& .fc': {
          fontFamily: 'inherit',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        },
        '& .fc-toolbar': {
          mb: 3,
          p: 2,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(102, 126, 234, 0.05)',
          borderBottom: `1px solid ${theme.palette.divider}`
        },
        '& .fc-toolbar-title': {
          fontSize: '1.75rem',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        },
        '& .fc-button': {
          borderRadius: '12px',
          border: 'none',
          fontWeight: 600,
          textTransform: 'none',
          px: 3,
          py: 1.5,
          fontSize: '0.9rem',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.3s ease'
        },
        '& .fc-button-primary': {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
          },
          '&:not(:disabled):active': {
            transform: 'translateY(0px)'
          }
        },
        '& .fc-daygrid-day': {
          borderColor: theme.palette.divider,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(102, 126, 234, 0.02)'
          }
        },
        '& .fc-day-today': {
          backgroundColor: `${theme.palette.mode === 'dark' ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.08)'} !important`,
          border: `2px solid ${theme.palette.primary.main} !important`
        },
        '& .fc-daygrid-day-number': {
          fontWeight: 600,
          color: theme.palette.text.primary
        },
        '& .fc-col-header-cell': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(102, 126, 234, 0.05)',
          fontWeight: 700,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider
        }
      }}>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={events}
          height={650}
          eventContent={renderEventContent}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth'
          }}
        />
      </Box>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box sx={{ width: '80%', margin: '5% auto', background: '#121212', padding: 3, borderRadius: 2 }}>
          <AddTrade
            initialData={selectedTrade}
            onClose={() => setModalOpen(false)}
            onRefresh={fetchTrades}
          />
        </Box>
      </Modal>
        <Modal open={addTradeOpen} onClose={() => setAddTradeOpen(false)}>
<Box
    sx={{
      width: '90%',
      maxWidth: 600,
      margin: '5% auto',
      background: '#121212',
      padding: 3,
      borderRadius: 2,
      maxHeight: '90vh',
      overflowY: 'auto'
    }}
        >
            <TradeForm
            onClose={() => setAddTradeOpen(false)}
            onRefresh={fetchTrades}
            />
        </Box>
        </Modal>


    </Box>
  );
};

export default TradeCalendar;
